import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import BuyerPublicNavbar from "@/components/BuyerPublicNavbar";
import BuyerSelectedMaterialsTable from "@/components/BuyerSelectedMaterialsTable";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Package,
  HardHat,
  Plus,
  Minus,
  CheckCircle,
  MagnifyingGlass,
  NotePencil,
  ShoppingCart,
  Cube,
  Pipe,
  Drop,
  DotsThree,
  ArrowLeft,
  Crane,
  Nut,
} from "@phosphor-icons/react";
import api, { publicAssetUrl } from "@/lib/api";
import { cn } from "@/lib/utils";
import { newLineSpec } from "@/lib/buyerRfqConstants";
import { parseProductSizes, catalogLineKey, catalogProductIdFromLineKey, formatLineSizeDisplay } from "@/lib/productSizes";
import { loadBuyerRfqDraft, mergeBuyerRfqDraft } from "@/lib/buyerRfqDraft";
import { setFloatingStack } from "@/lib/floatingStack";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function productUnitLabel(p) {
  const unitStr = p?.unit != null ? String(p.unit).trim() : "";
  if (unitStr) return unitStr;
  const n = String(p?.name || "").toLowerCase();
  if (/\bsq\.?\s*ft\b|\bsqft\b|\bm²\b|\bsqm\b/i.test(n)) return "per sqft";
  if (/set|system|cuplock|bundle/i.test(n)) return "per sets";
  return "per pcs";
}

function categoryTabIcon(category) {
  const c = String(category || "").toLowerCase();
  if (c.includes("scaffold") || c.includes("shutter"))
    return <HardHat size={18} weight="duotone" className="shrink-0 opacity-90" aria-hidden />;
  if (c.includes("cement")) return <Drop size={18} weight="duotone" className="shrink-0 opacity-90" aria-hidden />;
  if (c.includes("tmt") || c.includes("bar")) return <Cube size={18} weight="duotone" className="shrink-0 opacity-90" aria-hidden />;
  if (c.includes("rod") || c.includes("pipe")) return <Pipe size={18} weight="duotone" className="shrink-0 opacity-90" aria-hidden />;
  if (c.includes("other") || c === "general")
    return <DotsThree size={18} weight="bold" className="shrink-0 opacity-90" aria-hidden />;
  return <Package size={18} weight="duotone" className="shrink-0 opacity-90" aria-hidden />;
}

function parentCategoryHeroIcon(parent) {
  const slug = String(parent?.slug || "").toLowerCase();
  const name = String(parent?.name || "").toLowerCase();
  if (slug.includes("shutter") || name.includes("shutter"))
    return <Nut size={56} weight="duotone" className="text-[#FF7A00]" aria-hidden />;
  if (slug.includes("scaffold") || name.includes("scaffold"))
    return <Crane size={56} weight="duotone" className="text-[#FF7A00]" aria-hidden />;
  return <HardHat size={56} weight="duotone" className="text-[#FF7A00]" aria-hidden />;
}

function formatCategoryTypeLabel(raw) {
  const s = String(raw || "").trim();
  if (!s) return "Rent / Buy";
  const parts = s
    .split(/\s*\/\s*/)
    .map((p) => p.trim())
    .filter((p) => p.toLowerCase() !== "sell")
    .map(
      (p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()
    );
  if (parts.length === 0) return "Rent / Buy";
  return parts.join(" / ");
}

function withCatalogDefaults(product, size = "") {
  const sizeOpts = parseProductSizes(product?.sizes);
  const resolvedSize = String(size || "").trim() || (sizeOpts.length === 1 ? sizeOpts[0] : "");
  const lineKey = sizeOpts.length > 0 ? catalogLineKey(product.id, resolvedSize) : product.id;
  return {
    id: lineKey,
    catalogProductId: product.id,
    name: product.name,
    category: product.category,
    image_url: product.image_url,
    sizes: product.sizes,
    ...newLineSpec(),
    size: resolvedSize,
  };
}

function getProductCartLines(selectedProducts, productId) {
  return Object.entries(selectedProducts)
    .filter(([key, row]) => catalogProductIdFromLineKey(key, row) === productId)
    .map(([key, row]) => ({ key, row }));
}

function formatAddedLineLabel(ln, productName) {
  const sizePart = ln.size ? `${formatLineSizeDisplay(ln.size, productName)} · ` : "";
  if (ln.specType === "area") {
    return `${sizePart}${ln.areaValue || "—"} ${ln.areaUnit === "sqm" ? "m²" : "sq ft"}`;
  }
  return `${sizePart}×${ln.quantity ?? 1}`;
}

function prioritizeActiveSizeLines(productLines, activeSize) {
  if (!activeSize || productLines.length <= 1) return productLines;
  const active = productLines.find(({ row }) => row.size === activeSize);
  if (!active) return productLines;
  return [active, ...productLines.filter(({ key }) => key !== active.key)];
}

const ADDED_SIZE_CHIP_SLOT_CLASS = "mt-2 h-6 flex items-center gap-1 overflow-hidden flex-nowrap";

function AddedSizeChipSlot({ productLines, productName, activeSize, onSelectLine, testIdPrefix, reserveEmptySlot = false }) {
  if (productLines.length === 0) {
    return reserveEmptySlot ? <div className={ADDED_SIZE_CHIP_SLOT_CLASS} aria-hidden /> : null;
  }
  const ordered = prioritizeActiveSizeLines(productLines, activeSize);
  const [first, ...rest] = ordered;
  const overflowTitle = rest.map(({ row: ln }) => formatAddedLineLabel(ln, productName)).join(", ");

  return (
    <div className={ADDED_SIZE_CHIP_SLOT_CLASS} data-testid={testIdPrefix ? `${testIdPrefix}-sizes` : undefined}>
      <button
        type="button"
        className="min-w-0 max-w-[calc(100%-2.25rem)] truncate text-[10px] font-semibold rounded-full px-2 py-0.5 border border-orange-200 bg-orange-50 text-[#FF7A00] shrink"
        onClick={(e) => {
          e.stopPropagation();
          if (first.row.size) onSelectLine?.(first.row.size);
        }}
      >
        {formatAddedLineLabel(first.row, productName)}
      </button>
      {rest.length > 0 ? (
        <span
          className="text-[10px] font-bold rounded-full px-1.5 py-0.5 border border-neutral-200 bg-neutral-100 text-neutral-600 shrink-0 tabular-nums"
          title={overflowTitle}
        >
          +{rest.length}
        </span>
      ) : null}
    </div>
  );
}

function resolveProductLineKey(product, size, activeSizeByProduct) {
  const sizeOpts = parseProductSizes(product?.sizes);
  if (sizeOpts.length === 0) return product.id;
  const picked = String(size || activeSizeByProduct[product.id] || sizeOpts[0] || "").trim();
  if (!picked) return null;
  return catalogLineKey(product.id, picked);
}

export default function BuyerMaterialsPage() {
  const navigate = useNavigate();
  const { parentSlug: parentSlugParam } = useParams();
  const [products, setProducts] = useState([]);
  const [categoryNames, setCategoryNames] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [catalogSearchOpen, setCatalogSearchOpen] = useState(false);
  const [catalogSearchQuery, setCatalogSearchQuery] = useState("");
  const [catalogSearchCategory, setCatalogSearchCategory] = useState("__all__");
  const catalogSearchInputRef = useRef(null);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [activeMaterialCategory, setActiveMaterialCategory] = useState("");
  const [browseTree, setBrowseTree] = useState(null);
  const [qtyEditId, setQtyEditId] = useState(null);
  const [qtyEditText, setQtyEditText] = useState("");
  const [customLineName, setCustomLineName] = useState("");
  const [customLineSpecType, setCustomLineSpecType] = useState("quantity");
  const [customLineQtyDraft, setCustomLineQtyDraft] = useState("");
  const [customLineAreaValue, setCustomLineAreaValue] = useState("");
  const [customLineAreaUnit, setCustomLineAreaUnit] = useState("sqft");
  const [activeSizeByProduct, setActiveSizeByProduct] = useState({});

  const useHierarchy = (browseTree?.parents?.length ?? 0) > 0;

  const selectedParent = useMemo(() => {
    if (!useHierarchy || !browseTree?.parents?.length || !parentSlugParam) return null;
    return browseTree.parents.find((p) => String(p.slug ?? p.id) === parentSlugParam) ?? null;
  }, [useHierarchy, browseTree, parentSlugParam]);

  /** URL `/materials/:parentSlug` implies product grid; plain `/materials` is parent pickers — enables browser back/forward. */
  const browseStep =
    useHierarchy && parentSlugParam && selectedParent ? "products" : useHierarchy ? "parent" : "products";

  useEffect(() => {
    const d = loadBuyerRfqDraft();
    if (d?.selectedProducts && Object.keys(d.selectedProducts).length > 0) {
      setSelectedProducts(d.selectedProducts);
    }
    setBootstrapped(true);
  }, []);

  useEffect(() => {
    if (!bootstrapped) return;
    mergeBuyerRfqDraft({ selectedProducts });
  }, [bootstrapped, selectedProducts]);

  useEffect(() => {
    api
      .get("/products", { params: { limit: 300 } })
      .then((r) => {
        const raw = r.data.items ?? r.data;
        setProducts(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setProducts([]));
    api
      .get("/public/product-category-names")
      .then((r) => setCategoryNames(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCategoryNames([]));
    api
      .get("/public/product-categories/browse")
      .then((r) => setBrowseTree({ parents: Array.isArray(r.data?.parents) ? r.data.parents : [] }))
      .catch(() => setBrowseTree({ parents: [] }));
  }, []);

  useEffect(() => {
    if (!catalogSearchOpen) return;
    const t = requestAnimationFrame(() => catalogSearchInputRef.current?.focus());
    return () => cancelAnimationFrame(t);
  }, [catalogSearchOpen]);

  const catalogCategoryOptions = useMemo(() => {
    const set = new Set();
    for (const c of categoryNames) {
      const v = String(c || "").trim();
      if (v) set.add(v);
    }
    // Fallback: include any categories currently present on products.
    for (const p of products) {
      const c = String(p?.category || "").trim();
      if (c) set.add(c);
    }
    const arr = Array.from(set);
    arr.sort((a, b) => String(a).localeCompare(String(b)));
    return ["__all__", ...arr];
  }, [categoryNames, products]);

  const materialCategoryList = useMemo(() => {
    const set = new Set();
    for (const p of products) {
      const c = String(p?.category || "").trim() || "General";
      set.add(c);
    }
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [products]);

  useEffect(() => {
    if (useHierarchy) return;
    if (materialCategoryList.length === 0) {
      if (activeMaterialCategory) setActiveMaterialCategory("");
      return;
    }
    if (!materialCategoryList.includes(activeMaterialCategory)) {
      setActiveMaterialCategory(materialCategoryList[0]);
    }
  }, [materialCategoryList, activeMaterialCategory, useHierarchy]);

  useEffect(() => {
    if (!useHierarchy || browseTree === null || !parentSlugParam) return;
    const parent = browseTree.parents.find((p) => String(p.slug ?? p.id) === parentSlugParam);
    if (!parent) {
      toast.info("Category not found.");
      navigate("/materials", { replace: true });
      return;
    }
    const kids = parent.children;
    if (!Array.isArray(kids) || kids.length === 0) {
      toast.info("No subcategories are listed for this group yet. Use Search catalog to add a custom line.");
      navigate("/materials", { replace: true });
    }
  }, [useHierarchy, browseTree, parentSlugParam, navigate]);

  useEffect(() => {
    if (!selectedParent) {
      setActiveMaterialCategory("");
      return;
    }
    const kids = selectedParent.children;
    if (!Array.isArray(kids) || kids.length === 0) return;
    const sorted = [...kids].sort(
      (a, b) => (Number(a.sort_order) || 9999) - (Number(b.sort_order) || 9999)
    );
    const firstName = String(sorted[0]?.name || "").trim();
    setActiveMaterialCategory((prev) => {
      const ok = sorted.some((c) => String(c?.name || "").trim() === prev);
      return ok ? prev : firstName;
    });
  }, [selectedParent]);

  const filteredMaterialProducts = useMemo(() => {
    if (useHierarchy && browseStep === "products" && activeMaterialCategory) {
      const cat = activeMaterialCategory;
      return products.filter((p) => (String(p?.category || "").trim() || "General") === cat);
    }
    if (materialCategoryList.length === 0) return [];
    const cat = activeMaterialCategory || materialCategoryList[0];
    return products.filter((p) => (String(p?.category || "").trim() || "General") === cat);
  }, [products, materialCategoryList, activeMaterialCategory, useHierarchy, browseStep]);

  const cartBadgeTotal = useMemo(
    () =>
      Object.values(selectedProducts).reduce((sum, p) => {
        if (p.specType === "area") return sum + 1;
        const q = Math.floor(Number(p.quantity));
        return sum + (Number.isFinite(q) && q > 0 ? q : 0);
      }, 0),
    [selectedProducts]
  );

  const catalogSearchResults = useMemo(() => {
    const q = catalogSearchQuery.trim().toLowerCase();
    const cat = String(catalogSearchCategory || "__all__");
    const pool =
      cat === "__all__"
        ? products
        : products.filter((p) => String(p?.category || "").trim() === cat);
    if (!q) return pool;
    return pool.filter((p) => {
      const name = String(p?.name || "").toLowerCase();
      const pc = String(p?.category || "").toLowerCase();
      return name.includes(q) || pc.includes(q);
    });
  }, [catalogSearchQuery, catalogSearchCategory, products]);

  const resetCustomLineForm = () => {
    setCustomLineName("");
    setCustomLineSpecType("quantity");
    setCustomLineQtyDraft("");
    setCustomLineAreaValue("");
    setCustomLineAreaUnit("sqft");
  };

  const addCustomLineToRequirement = () => {
    const name = customLineName.trim();
    if (!name) {
      toast.error("Enter a product name");
      return;
    }
    const id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? `custom:${crypto.randomUUID()}`
        : `custom:${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    if (customLineSpecType === "area") {
      let raw = String(customLineAreaValue ?? "").replace(/,/g, "").trim();
      const dot = raw.indexOf(".");
      if (dot !== -1) {
        raw = raw.slice(0, dot + 1) + raw.slice(dot + 1).replace(/\./g, "");
      }
      const av = parseFloat(raw);
      if (!Number.isFinite(av) || av <= 0) {
        toast.error("Enter a valid area greater than 0");
        return;
      }
      setSelectedProducts((prev) => ({
        ...prev,
        [id]: {
          id,
          name,
          category: "Custom",
          image_url: "",
          isCustom: true,
          ...newLineSpec(),
          specType: "area",
          areaValue: raw,
          areaUnit: customLineAreaUnit === "sqm" ? "sqm" : "sqft",
          quantity: 1,
        },
      }));
    } else {
      const q = parseInt(String(customLineQtyDraft).trim(), 10);
      if (!Number.isFinite(q) || q < 1) {
        toast.error("Enter a valid quantity (at least 1)");
        return;
      }
      setSelectedProducts((prev) => ({
        ...prev,
        [id]: {
          id,
          name,
          category: "Custom",
          image_url: "",
          isCustom: true,
          ...newLineSpec(),
          specType: "quantity",
          quantity: q,
        },
      }));
    }
    resetCustomLineForm();
    toast.success("Added to your requirement");
  };

  const addCustomProductFromCatalog = () => {
    const name = catalogSearchQuery.trim();
    if (!name) {
      toast.error("Type what you need in the search box first");
      return;
    }
    const id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? `custom:${crypto.randomUUID()}`
        : `custom:${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setSelectedProducts((prev) => ({
      ...prev,
      [id]: {
        id,
        name,
        category: "Custom",
        image_url: "",
        isCustom: true,
        ...newLineSpec(),
      },
    }));
    setCatalogSearchQuery("");
    toast.success("Custom product added to your list");
  };

  const incrementProductFromCatalogSearch = (product, size) => {
    const lineKey = resolveProductLineKey(product, size, activeSizeByProduct);
    if (!lineKey) {
      toast.error("Select a size first");
      return;
    }
    const resolvedSize = lineKey.startsWith("catalog:") ? lineKey.split(":").slice(2).join(":") : "";
    setSelectedProducts((prev) => {
      const existing = prev[lineKey];
      if (existing?.specType === "area") {
        toast.info("Switch to Qty on this card to use +/−, or enter area below.");
        return prev;
      }
      if (existing) {
        return {
          ...prev,
          [lineKey]: {
            ...existing,
            quantity: (existing.quantity || 1) + 1,
          },
        };
      }
      return { ...prev, [lineKey]: withCatalogDefaults(product, resolvedSize) };
    });
  };

  const decrementProductFromCatalogSearch = (product, size) => {
    const lineKey = resolveProductLineKey(product, size, activeSizeByProduct);
    if (!lineKey) return;
    setSelectedProducts((prev) => {
      const cur = prev[lineKey];
      if (!cur) return prev;
      if (cur.specType === "area") {
        const next = { ...prev };
        delete next[lineKey];
        return next;
      }
      if (cur.quantity <= 1) {
        const next = { ...prev };
        delete next[lineKey];
        return next;
      }
      return {
        ...prev,
        [lineKey]: { ...cur, quantity: cur.quantity - 1 },
      };
    });
  };

  const setMaterialLineQuantityFromCard = (product, n, size) => {
    const lineKey = resolveProductLineKey(product, size, activeSizeByProduct);
    if (!lineKey) return;
    const resolvedSize = lineKey.startsWith("catalog:") ? lineKey.split(":").slice(2).join(":") : "";
    const q = Math.max(0, Math.min(1_000_000, Math.floor(Number(n))));
    if (!Number.isFinite(q)) return;
    setSelectedProducts((prev) => {
      const cur = prev[lineKey];
      if (q === 0) {
        if (!cur) return prev;
        const next = { ...prev };
        delete next[lineKey];
        return next;
      }
      if (cur?.specType === "area") {
        return prev;
      }
      if (cur) {
        return { ...prev, [lineKey]: { ...cur, quantity: q, specType: "quantity" } };
      }
      return { ...prev, [lineKey]: { ...withCatalogDefaults(product, resolvedSize), quantity: q } };
    });
  };

  const setMaterialLineSpecType = (product, specType, size) => {
    const lineKey = resolveProductLineKey(product, size, activeSizeByProduct);
    if (!lineKey) return;
    const resolvedSize = lineKey.startsWith("catalog:") ? lineKey.split(":").slice(2).join(":") : "";
    setQtyEditId(null);
    setQtyEditText("");
    setSelectedProducts((prev) => {
      const row = prev[lineKey];
      if (!row) {
        if (specType !== "area") return prev;
        return {
          ...prev,
          [lineKey]: {
            ...withCatalogDefaults(product, resolvedSize),
            specType: "area",
            areaValue: "",
            areaUnit: "sqft",
            quantity: 1,
          },
        };
      }
      if (specType === "area") {
        return {
          ...prev,
          [lineKey]: {
            ...row,
            specType: "area",
            areaValue: row.areaValue ?? "",
            areaUnit: row.areaUnit || "sqft",
            quantity: Math.max(1, row.quantity || 1),
          },
        };
      }
      return {
        ...prev,
        [lineKey]: {
          ...row,
          specType: "quantity",
          quantity: Math.max(1, row.quantity || 1),
          areaValue: "",
          areaUnit: "sqft",
        },
      };
    });
  };

  const goToRequirement = () => {
    mergeBuyerRfqDraft({ selectedProducts });
    navigate("/requirement");
  };

  const selectedCount = Object.keys(selectedProducts).length;

  useEffect(() => {
    setFloatingStack(selectedCount > 0 ? 1 : 0);
    return () => setFloatingStack(0);
  }, [selectedCount]);

  const openParentCategory = (parent) => {
    const kids = parent?.children;
    if (!Array.isArray(kids) || kids.length === 0) {
      toast.info("No subcategories are listed for this group yet. Use Search catalog to add a custom line.");
      return;
    }
    const sorted = [...kids].sort(
      (a, b) => (Number(a.sort_order) || 9999) - (Number(b.sort_order) || 9999)
    );
    const first = sorted[0];
    const name = String(first?.name || "").trim();
    if (!name) {
      toast.info("No subcategories are listed for this group yet. Use Search catalog to add a custom line.");
      return;
    }
    const slug = encodeURIComponent(String(parent.slug ?? parent.id));
    navigate(`/materials/${slug}`);
  };

  const openSubcategory = (child) => {
    const name = String(child?.name || "").trim();
    if (!name) return;
    setActiveMaterialCategory(name);
  };

  const goBackBrowse = () => {
    if (!useHierarchy || !parentSlugParam) return;
    navigate(-1);
  };

  const openCatalogSearch = () => {
    setCatalogSearchQuery("");
    setCatalogSearchOpen(true);
  };

  const showMaterialsGrid = !useHierarchy || (useHierarchy && browseStep === "products");

  return (
    <div className="min-h-screen" style={{ background: "#F7F4EF" }}>
      <BuyerPublicNavbar />

      {/* RFQ Form */}
      <section id="rfq-form" className="py-10 sm:py-14" data-testid="rfq-form-section">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {browseTree === null ? (
            <p className="text-sm text-center py-20" style={{ color: "#bbb" }} data-testid="materials-loading">
              Loading catalog…
            </p>
          ) : (
            <>
              {useHierarchy && browseStep === "parent" ? (
                <>
                  <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-4" style={{ color: "#FF7A00" }}>Step 1 — Select category</p>
                  <div className="rounded-2xl bg-white mb-6" style={{ border: "1px solid #E8E3D9", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
                    <div className="flex flex-row flex-nowrap items-center gap-2 sm:gap-3 p-4 sm:p-5 border-b" style={{ borderColor: "#F0EBE3" }}>
                      <Link
                        to="/"
                        data-testid="materials-back-home-btn"
                        className="inline-flex items-center gap-1.5 shrink-0 h-10 px-4 rounded-full text-sm font-semibold transition-colors hover:bg-neutral-100"
                        style={{ background: "#F0EBE3", color: "#555" }}
                      >
                        <ArrowLeft size={16} weight="bold" aria-hidden />
                        Back
                      </Link>
                      <button
                        type="button"
                        data-testid="search-catalog-toolbar-btn"
                        onClick={openCatalogSearch}
                        className="flex-1 flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold justify-start transition-colors hover:bg-neutral-50"
                        style={{ background: "#F7F4EF", border: "1.5px solid #E8E3D9", color: "#888" }}
                      >
                        <MagnifyingGlass size={16} weight="bold" className="shrink-0" style={{ color: "#FF7A00" }} aria-hidden />
                        <span className="truncate">Search catalog</span>
                      </button>
                    </div>
                    <div className="p-5 sm:p-8">
                      <h2 className="font-black text-neutral-900 leading-tight tracking-tighter mb-2" style={{ fontSize: "clamp(1.4rem, 2.5vw, 1.9rem)" }}>What do you need?</h2>
                      <p className="text-neutral-400 text-sm mb-7">Choose a category, then select specific products.</p>
                      <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-md sm:max-w-xl md:max-w-2xl mx-auto">
                        {browseTree.parents.map((parent) => (
                          <button
                            key={parent.id}
                            type="button"
                            data-testid={`parent-category-card-${String(parent.slug || parent.id).replace(/\s+/g, "-")}`}
                            onClick={() => openParentCategory(parent)}
                            className="text-left rounded-2xl p-4 sm:p-5 transition-all hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF7A00] focus-visible:ring-offset-2 min-w-0 group"
                            style={{ background: "#FDFCF9", border: "1.5px solid #E8E3D9" }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = "#FF7A00"}
                            onMouseLeave={e => e.currentTarget.style.borderColor = "#E8E3D9"}
                          >
                            <div className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-xl mb-3 overflow-hidden" style={{ background: "rgba(255,122,0,0.08)" }}>
                              <span className="flex scale-[0.5] sm:scale-[0.6] origin-center [&_svg]:shrink-0">
                                {parentCategoryHeroIcon(parent)}
                              </span>
                            </div>
                            <p className="text-sm sm:text-base font-black text-neutral-900 leading-snug line-clamp-2">
                              {parent.name}
                            </p>
                            <p className="text-[10px] sm:text-xs font-bold mt-1 leading-tight" style={{ color: "#FF7A00" }}>
                              {formatCategoryTypeLabel(parent.type)}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              ) : null}

              {showMaterialsGrid ? (
                <>
                  {!useHierarchy ? (
                    <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-4" style={{ color: "#FF7A00" }}>Step 1 — Select materials</p>
                  ) : null}

                  <div className="rounded-2xl bg-white mb-6" style={{ border: "1px solid #E8E3D9", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
                    {useHierarchy ? (
                      <div className="space-y-3 min-w-0 p-4 sm:p-5 border-b" style={{ borderColor: "#F0EBE3" }}>
                        <div className="flex flex-row flex-nowrap items-center gap-2 sm:gap-3 min-w-0">
                          <button
                            type="button"
                            data-testid="materials-back-parent-btn"
                            onClick={goBackBrowse}
                            className="inline-flex items-center gap-1.5 shrink-0 h-10 px-4 rounded-full text-sm font-semibold transition-colors"
                            style={{ background: "#F0EBE3", color: "#555" }}
                          >
                            <ArrowLeft size={16} weight="bold" aria-hidden />
                            Back
                          </button>
                          <button
                            type="button"
                            data-testid="search-catalog-toolbar-btn-grid"
                            onClick={openCatalogSearch}
                            className="flex-1 flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold justify-start transition-colors hover:bg-neutral-50"
                            style={{ background: "#F7F4EF", border: "1.5px solid #E8E3D9", color: "#888" }}
                          >
                            <MagnifyingGlass size={16} weight="bold" className="shrink-0" style={{ color: "#FF7A00" }} aria-hidden />
                            <span className="truncate">Search catalog</span>
                          </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm px-1">
                          <span className="font-black text-neutral-900">{selectedParent?.name}</span>
                          <span style={{ color: "#E8E3D9" }} aria-hidden>/</span>
                          <span className="font-semibold" style={{ color: "#FF7A00" }}>{activeMaterialCategory}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 sm:p-5 border-b" style={{ borderColor: "#F0EBE3" }}>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <h2 className="font-black text-neutral-900 leading-tight tracking-tight" style={{ fontSize: "clamp(1.2rem, 2vw, 1.5rem)" }}>Select Materials</h2>
                            <p className="text-neutral-400 text-xs mt-0.5">Choose a category and add items to your list</p>
                          </div>
                          <button
                            type="button"
                            data-testid="search-catalog-toolbar-btn"
                            onClick={openCatalogSearch}
                            className="flex-1 sm:flex-none flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold justify-start sm:justify-center transition-colors hover:bg-neutral-50"
                            style={{ background: "#F7F4EF", border: "1.5px solid #E8E3D9", color: "#888", minWidth: "160px" }}
                          >
                            <MagnifyingGlass size={16} weight="bold" className="shrink-0" style={{ color: "#FF7A00" }} aria-hidden />
                            <span className="truncate">Search catalog</span>
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="p-4 sm:p-6">
                    {!useHierarchy && materialCategoryList.length > 0 ? (
                      <div
                        className="flex flex-wrap gap-2 mb-5 md:mb-6"
                        role="tablist"
                        aria-label="Material categories"
                      >
                        {materialCategoryList.map((cat) => {
                          const active = cat === (activeMaterialCategory || materialCategoryList[0]);
                          return (
                            <button
                              key={cat}
                              type="button"
                              role="tab"
                              aria-selected={active}
                              data-testid={`material-category-tab-${String(cat).replace(/\s+/g, "-")}`}
                              onClick={() => setActiveMaterialCategory(cat)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-all min-h-[36px]"
                              style={{
                                background: active ? "#FF7A00" : "#F0EBE3",
                                color: active ? "#fff" : "#666",
                                border: active ? "1.5px solid #FF7A00" : "1.5px solid #E8E3D9",
                              }}
                            >
                              <span className="shrink-0 inline-flex [&_svg]:text-current">{categoryTabIcon(cat)}</span>
                              <span className="leading-snug">{cat}</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}

                    {useHierarchy && selectedParent?.children?.length ? (
                      <div
                        className="flex flex-wrap gap-2 mb-5 md:mb-6"
                        role="tablist"
                        aria-label="Subcategories"
                      >
                        {selectedParent.children.map((child) => {
                          const active = child.name === activeMaterialCategory;
                          return (
                            <button
                              key={child.id}
                              type="button"
                              role="tab"
                              aria-selected={active}
                              data-testid={`material-subcategory-tab-${String(child.name).replace(/\s+/g, "-")}`}
                              onClick={() => openSubcategory(child)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-all min-h-[36px]"
                              style={{
                                background: active ? "#FF7A00" : "#F0EBE3",
                                color: active ? "#fff" : "#666",
                                border: active ? "1.5px solid #FF7A00" : "1.5px solid #E8E3D9",
                              }}
                            >
                              <span className="shrink-0 inline-flex [&_svg]:text-current">{categoryTabIcon(child.name)}</span>
                              <span className="leading-snug">{child.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
                      {filteredMaterialProducts.map((p) => {
                        const sizeOpts = parseProductSizes(p.sizes);
                        const hasSizes = sizeOpts.length > 0;
                        const activeSize = hasSizes ? (activeSizeByProduct[p.id] || sizeOpts[0]) : "";
                        const lineKey = resolveProductLineKey(p, activeSize, activeSizeByProduct) || p.id;
                        const productLines = getProductCartLines(selectedProducts, p.id);
                        const row = selectedProducts[lineKey];
                        const isAdded = productLines.length > 0;
                        const isAreaLine = row?.specType === "area";
                        const displayQtyStr =
                          row && !isAreaLine
                            ? String(Math.max(0, Math.floor(Number(row.quantity) || 0)))
                            : "0";
                        const unitHint =
                          row?.specType === "area"
                            ? row.areaUnit === "sqm"
                              ? "m²"
                              : "sq ft"
                            : productUnitLabel(p);
                        const specAndSizeRow = (
                          <div className="flex items-center gap-1.5 mt-2 min-w-0">
                            <div className="inline-flex rounded-lg border border-neutral-200 overflow-hidden bg-white shadow-sm shrink-0">
                              <button
                                type="button"
                                data-testid={`material-card-spec-qty-${p.id}`}
                                className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                                  !isAreaLine ? "bg-[#FF7A00] text-white" : "bg-white text-neutral-600"
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMaterialLineSpecType(p, "quantity", activeSize);
                                }}
                              >
                                Qty
                              </button>
                              <button
                                type="button"
                                data-testid={`material-card-spec-area-${p.id}`}
                                className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide border-l border-neutral-200 ${
                                  isAreaLine ? "bg-[#FF7A00] text-white" : "bg-white text-neutral-600"
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMaterialLineSpecType(p, "area", activeSize);
                                }}
                              >
                                Area
                              </button>
                            </div>
                            {hasSizes ? (
                              <Select
                                value={activeSize || sizeOpts[0]}
                                onValueChange={(v) =>
                                  setActiveSizeByProduct((prev) => ({ ...prev, [p.id]: v }))
                                }
                              >
                                <SelectTrigger
                                  className="h-7 min-w-0 flex-1 rounded-lg border-neutral-200 text-[10px] font-semibold px-2"
                                  data-testid={`material-card-size-${p.id}`}
                                >
                                  <SelectValue placeholder="Size" />
                                </SelectTrigger>
                                <SelectContent>
                                  {sizeOpts.map((s) => (
                                    <SelectItem key={s} value={s}>{formatLineSizeDisplay(s, p.name)}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : null}
                          </div>
                        );
                        const addedSizesRow = hasSizes ? (
                          <AddedSizeChipSlot
                            productLines={productLines}
                            productName={p.name}
                            activeSize={activeSize}
                            onSelectLine={(size) =>
                              setActiveSizeByProduct((prev) => ({ ...prev, [p.id]: size }))
                            }
                            testIdPrefix={`material-card-${p.id}`}
                            reserveEmptySlot
                          />
                        ) : null;
                        const qtyRow = (
                          <div className="flex items-center justify-between gap-2 pt-1">
                            <button
                              type="button"
                              data-testid={`material-card-minus-${p.id}`}
                              disabled={!row}
                              aria-label="Decrease quantity or remove"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (row) decrementProductFromCatalogSearch(p, activeSize);
                              }}
                              className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 disabled:opacity-35 disabled:pointer-events-none"
                            >
                              <Minus size={14} weight="bold" />
                            </button>
                            <Input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              aria-label="Quantity"
                              data-testid={`material-card-qty-${p.id}`}
                              value={qtyEditId === lineKey ? qtyEditText : displayQtyStr}
                              onFocus={(e) => {
                                setQtyEditId(lineKey);
                                setQtyEditText(displayQtyStr);
                                e.target.select();
                              }}
                              onChange={(e) => {
                                const cleaned = e.target.value.replace(/\D/g, "");
                                setQtyEditText(cleaned);
                                if (cleaned === "") return;
                                const n = parseInt(cleaned, 10);
                                if (!Number.isFinite(n)) return;
                                setMaterialLineQuantityFromCard(p, n, activeSize);
                              }}
                              onBlur={() => {
                                const raw = qtyEditId === lineKey ? qtyEditText : displayQtyStr;
                                setQtyEditId(null);
                                setQtyEditText("");
                                const cleaned = String(raw).replace(/\D/g, "");
                                const n = cleaned === "" ? 0 : parseInt(cleaned, 10);
                                setMaterialLineQuantityFromCard(p, Number.isFinite(n) ? n : 0, activeSize);
                              }}
                              className="h-8 w-12 min-w-[2.75rem] px-1 text-center text-sm font-semibold tabular-nums text-neutral-900 border-neutral-200 rounded-lg [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            />
                            <button
                              type="button"
                              data-testid={`material-card-plus-${p.id}`}
                              disabled={!!row && row.specType === "area"}
                              aria-label="Increase quantity or add"
                              onClick={(e) => {
                                e.stopPropagation();
                                incrementProductFromCatalogSearch(p, activeSize);
                              }}
                              className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 disabled:opacity-35 disabled:pointer-events-none"
                            >
                              <Plus size={14} weight="bold" />
                            </button>
                          </div>
                        );
                        const areaRow = (
                          <div className="flex flex-col gap-2 pt-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Input
                                type="text"
                                inputMode="decimal"
                                placeholder="Area"
                                data-testid={`material-card-area-${p.id}`}
                                className="h-8 min-w-0 flex-1 text-sm font-mono border-neutral-200 rounded-lg px-2"
                                value={row?.areaValue ?? ""}
                                onChange={(e) => {
                                  let v = e.target.value.replace(/[^\d.]/g, "");
                                  const dot = v.indexOf(".");
                                  if (dot !== -1) {
                                    v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, "");
                                  }
                                  setSelectedProducts((prev) => {
                                    const cur = prev[lineKey];
                                    if (!cur) return prev;
                                    return { ...prev, [lineKey]: { ...cur, areaValue: v } };
                                  });
                                }}
                              />
                              <Select
                                value={row?.areaUnit === "sqm" ? "sqm" : "sqft"}
                                onValueChange={(v) =>
                                  setSelectedProducts((prev) => {
                                    const cur = prev[lineKey];
                                    if (!cur) return prev;
                                    return { ...prev, [lineKey]: { ...cur, areaUnit: v } };
                                  })
                                }
                              >
                                <SelectTrigger
                                  className="h-8 w-[4.5rem] shrink-0 rounded-lg border-neutral-200 text-xs px-2"
                                  data-testid={`material-card-area-unit-${p.id}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="sqft">sq ft</SelectItem>
                                  <SelectItem value="sqm">m²</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <button
                              type="button"
                              data-testid={`material-card-area-remove-${p.id}`}
                              aria-label="Remove line"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (row) decrementProductFromCatalogSearch(p, activeSize);
                              }}
                              className="text-xs font-semibold text-neutral-500 hover:text-red-600 self-start underline-offset-2 hover:underline"
                            >
                              Remove
                            </button>
                          </div>
                        );
                        return (
                          <div
                            key={p.id}
                            data-testid={`product-card-${p.id}`}
                            data-added={isAdded ? "true" : "false"}
                            className="relative flex flex-col justify-between rounded-2xl bg-white p-4 min-h-[200px] transition-all"
                            style={{
                              border: isAdded ? "2px solid #FF7A00" : "1.5px solid #E8E3D9",
                              boxShadow: isAdded ? "0 4px 16px rgba(255,122,0,0.14)" : "0 1px 4px rgba(0,0,0,0.04)",
                            }}
                          >
                            <div
                              className="aspect-[4/3] w-full rounded-lg overflow-hidden bg-neutral-100 mb-3"
                              data-testid={`material-card-image-${p.id}`}
                            >
                              {p.image_url ? (
                                <img
                                  src={publicAssetUrl(p.image_url)}
                                  alt={p.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package size={36} className="text-neutral-300" weight="duotone" aria-hidden />
                                </div>
                              )}
                            </div>
                            <div className="flex items-start justify-between gap-2 pr-0 min-h-[2.5rem]">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold leading-snug text-neutral-900 line-clamp-2">{p.name}</p>
                                <p className="text-xs text-neutral-400 mt-1">{unitHint}</p>
                              </div>
                              {isAdded ? (
                                <span
                                  data-testid={`material-card-added-badge-${p.id}`}
                                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-orange-200/80 bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-[#FF7A00]"
                                >
                                  <CheckCircle size={14} weight="fill" className="shrink-0" aria-hidden />
                                  Added
                                </span>
                              ) : null}
                            </div>
                            {specAndSizeRow}
                            {addedSizesRow}
                            <div className="mt-2">
                              {isAdded && isAreaLine ? areaRow : qtyRow}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="rounded-2xl overflow-hidden mt-6 sm:mt-8" style={{ background: "#FDFCF9", border: "1.5px solid #E8E3D9" }}>
                      <div className="p-5 space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,122,0,0.1)" }}>
                            <Cube size={20} weight="duotone" className="text-[#FF7A00]" aria-hidden />
                          </div>
                          <div>
                            <h2 className="text-sm font-black text-neutral-900 leading-tight">
                              Didn&apos;t find your product?
                            </h2>
                            <p className="text-xs mt-0.5" style={{ color: "#999" }}>Add a custom line to your requirement</p>
                          </div>
                        </div>
                        <div className="flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                          <div className="min-w-0 flex-1 sm:min-w-[12rem]">
                            <label htmlFor="materials-custom-name-input" className="sr-only">
                              Product name
                            </label>
                            <Input
                              id="materials-custom-name-input"
                              data-testid="materials-custom-name"
                              placeholder="e.g. Steel Mesh 6×6"
                              value={customLineName}
                              onChange={(e) => setCustomLineName(e.target.value)}
                              className="rounded-lg border-neutral-200 h-10 w-full min-w-0 text-sm"
                            />
                          </div>
                          <div className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-2 sm:gap-x-3">
                            <span className="text-xs font-medium text-neutral-600 whitespace-nowrap">
                              Specify by
                            </span>
                            <div className="inline-flex rounded-lg border border-neutral-200 overflow-hidden bg-white shadow-sm">
                              <button
                                type="button"
                                data-testid="materials-custom-spec-qty"
                                className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wide ${
                                  customLineSpecType === "quantity"
                                    ? "bg-[#FF7A00] text-white"
                                    : "bg-white text-neutral-600"
                                }`}
                                onClick={() => setCustomLineSpecType("quantity")}
                              >
                                Qty
                              </button>
                              <button
                                type="button"
                                data-testid="materials-custom-spec-area"
                                className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wide border-l border-neutral-200 ${
                                  customLineSpecType === "area"
                                    ? "bg-[#FF7A00] text-white"
                                    : "bg-white text-neutral-600"
                                }`}
                                onClick={() => setCustomLineSpecType("area")}
                              >
                                Area
                              </button>
                            </div>
                            {customLineSpecType === "quantity" ? (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  data-testid="materials-custom-qty-minus"
                                  aria-label="Decrease quantity"
                                  onClick={() => {
                                    const cur = Math.max(
                                      1,
                                      parseInt(String(customLineQtyDraft).trim(), 10) || 1
                                    );
                                    setCustomLineQtyDraft(String(Math.max(1, cur - 1)));
                                  }}
                                  className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                                >
                                  <Minus size={14} weight="bold" />
                                </button>
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="off"
                                  data-testid="materials-custom-qty"
                                  placeholder="Qty"
                                  aria-label="Quantity"
                                  value={customLineQtyDraft}
                                  onChange={(e) => {
                                    const cleaned = e.target.value.replace(/\D/g, "");
                                    setCustomLineQtyDraft(cleaned);
                                  }}
                                  className="h-9 w-14 min-w-[3rem] text-center text-sm font-semibold tabular-nums border-neutral-200 rounded-lg"
                                />
                                <button
                                  type="button"
                                  data-testid="materials-custom-qty-plus"
                                  aria-label="Increase quantity"
                                  onClick={() => {
                                    const cur = Math.max(
                                      1,
                                      parseInt(String(customLineQtyDraft).trim(), 10) || 1
                                    );
                                    setCustomLineQtyDraft(String(cur + 1));
                                  }}
                                  className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                                >
                                  <Plus size={14} weight="bold" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  placeholder="Area"
                                  data-testid="materials-custom-area"
                                  aria-label="Area amount"
                                  className="h-9 w-[5.5rem] sm:w-24 shrink-0 text-sm font-mono border-neutral-200 rounded-lg px-2"
                                  value={customLineAreaValue}
                                  onChange={(e) => {
                                    let v = e.target.value.replace(/[^\d.]/g, "");
                                    const dot = v.indexOf(".");
                                    if (dot !== -1) {
                                      v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, "");
                                    }
                                    setCustomLineAreaValue(v);
                                  }}
                                />
                                <Select
                                  value={customLineAreaUnit === "sqm" ? "sqm" : "sqft"}
                                  onValueChange={(v) => setCustomLineAreaUnit(v)}
                                >
                                  <SelectTrigger
                                    className="h-9 w-[5rem] shrink-0 rounded-lg border-neutral-200 text-xs"
                                    data-testid="materials-custom-area-unit"
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="sqft">sq ft</SelectItem>
                                    <SelectItem value="sqm">m²</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                          <div className="flex shrink-0 justify-start sm:justify-end sm:ml-auto sm:pl-0">
                            <button
                              type="button"
                              data-testid="materials-custom-add-btn"
                              className="h-10 w-full sm:w-auto px-5 rounded-full text-sm font-bold whitespace-nowrap transition-colors hover:opacity-90"
                              style={{ background: "#FF7A00", color: "#fff" }}
                              onClick={addCustomLineToRequirement}
                            >
                              Add to requirement
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {!useHierarchy && materialCategoryList.length === 0 ? (
                      <p className="text-sm mt-6 text-center" style={{ color: "#aaa" }}>
                        No catalog products yet. Use Search catalog to add a custom item.
                      </p>
                    ) : null}
                    {useHierarchy && browseStep === "products" && filteredMaterialProducts.length === 0 ? (
                      <p className="text-sm mt-6 text-center" style={{ color: "#aaa" }}>
                        No catalog items in <span className="font-semibold text-neutral-700">{activeMaterialCategory}</span> yet.
                        Try another subcategory or use Search catalog.
                      </p>
                    ) : null}
                    </div>{/* end padding wrapper */}
                  </div>
                </>
              ) : null}
            </>
          )}

          {selectedCount > 0 ? (
            <BuyerSelectedMaterialsTable
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
              testIdPrefix="materials-cart"
              className="rounded-2xl border-neutral-200 shadow-sm overflow-hidden mb-6"
              subtitle="Review quantities and specs here. Open View Enquiry when ready to submit."
            />
          ) : null}

          <Dialog
            open={catalogSearchOpen}
            onOpenChange={(open) => {
              setCatalogSearchOpen(open);
              if (!open) {
                setCatalogSearchQuery("");
                setCatalogSearchCategory("__all__");
              }
            }}
          >
            <DialogContent
              className="max-w-lg rounded-sm border-neutral-200 sm:rounded-sm gap-0 p-0 overflow-hidden"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <DialogHeader className="p-6 pb-4 space-y-1.5">
                <DialogTitle className="text-left">Search catalog</DialogTitle>
                <DialogDescription className="text-left">
                  Type to filter the catalog. Use + and − to change units (quantity mode). After adding, open View Enquiry to switch a line to area (sq ft or m²) and complete your requirement. If nothing matches, use{" "}
                  <span className="font-medium text-neutral-800">Didn&apos;t find your product?</span> on this page or add from your search text below.
                </DialogDescription>
              </DialogHeader>
              <div className="px-6 pb-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="sm:col-span-1">
                    <Select
                      value={catalogCategoryOptions.includes(catalogSearchCategory) ? catalogSearchCategory : "__all__"}
                      onValueChange={(v) => setCatalogSearchCategory(v)}
                    >
                      <SelectTrigger className="rounded-sm border-neutral-300" data-testid="catalog-category-select">
                        <SelectValue placeholder="All categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All categories</SelectItem>
                        {catalogCategoryOptions
                          .filter((c) => c !== "__all__")
                          .map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="relative sm:col-span-2">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" size={18} weight="bold" />
                    <Input
                      ref={catalogSearchInputRef}
                      data-testid="catalog-search-input"
                      placeholder="Search by name…"
                      value={catalogSearchQuery}
                      onChange={(e) => setCatalogSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        if (catalogSearchResults.length === 0 && catalogSearchQuery.trim()) {
                          addCustomProductFromCatalog();
                        }
                      }}
                      className="pl-10 rounded-sm border-neutral-300"
                    />
                  </div>
                </div>
              </div>
              <div
                className="border-t border-neutral-200 bg-neutral-50 max-h-[min(50vh,320px)] overflow-y-auto mx-0"
                data-testid="catalog-search-results"
              >
                {catalogSearchResults.length === 0 ? (
                  <div className="p-4 text-center space-y-3">
                    <p className="text-sm text-neutral-600 font-medium">No products match your search.</p>
                    {catalogSearchQuery.trim() ? (
                      <>
                        <p className="text-xs text-neutral-500">
                          Add this as a custom product using the name you typed above.
                        </p>
                        <Button
                          type="button"
                          data-testid="catalog-add-custom-btn"
                          onClick={addCustomProductFromCatalog}
                          className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white font-bold w-full sm:w-auto"
                        >
                          <NotePencil size={16} weight="bold" className="mr-1.5" />
                          Add &quot;{catalogSearchQuery.trim()}&quot; as custom product
                        </Button>
                      </>
                    ) : (
                      <p className="text-xs text-neutral-500">Try another search term, or type what you need and press Enter or use the button when there are no matches.</p>
                    )}
                  </div>
                ) : (
                  <ul className="py-1">
                    {catalogSearchResults.map((p) => {
                      const sizeOpts = parseProductSizes(p.sizes);
                      const hasSizes = sizeOpts.length > 0;
                      const activeSize = hasSizes ? (activeSizeByProduct[p.id] || sizeOpts[0]) : "";
                      const lineKey = resolveProductLineKey(p, activeSize, activeSizeByProduct) || p.id;
                      const productLines = getProductCartLines(selectedProducts, p.id);
                      const inCart = productLines.length > 0;
                      const row = selectedProducts[lineKey];
                      return (
                      <li key={p.id}>
                        <div
                          data-testid={`catalog-search-option-${p.id}`}
                          data-added={inCart ? "true" : "false"}
                          className={`w-full flex items-center gap-3 px-4 py-3 border-b border-neutral-100/80 last:border-0 transition-colors ${
                            inCart
                              ? "bg-orange-50 shadow-[inset_3px_0_0_0_#FF5A1F]"
                              : "bg-transparent"
                          }`}
                        >
                          <div
                            className={`w-11 h-11 rounded-sm overflow-hidden flex-shrink-0 ${
                              inCart
                                ? "ring-2 ring-orange-500 ring-offset-2 ring-offset-orange-50"
                                : "bg-neutral-200"
                            }`}
                          >
                            {p.image_url ? (
                              <img src={publicAssetUrl(p.image_url)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-neutral-200">
                                <Package size={22} className="text-neutral-400" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1 text-left">
                            <p className="text-sm font-bold leading-tight truncate">{p.name}</p>
                            <p className="text-xs text-neutral-500">{p.category}</p>
                            {hasSizes ? (
                              <div className="mt-1.5 max-w-[160px]">
                                <Select
                                  value={activeSize || sizeOpts[0]}
                                  onValueChange={(v) =>
                                    setActiveSizeByProduct((prev) => ({ ...prev, [p.id]: v }))
                                  }
                                >
                                  <SelectTrigger className="h-7 text-xs rounded-sm border-neutral-200" data-testid={`catalog-search-size-${p.id}`}>
                                    <SelectValue placeholder="Size" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {sizeOpts.map((s) => (
                                      <SelectItem key={s} value={s}>{formatLineSizeDisplay(s, p.name)}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            ) : null}
                            {hasSizes ? (
                              <AddedSizeChipSlot
                                productLines={productLines}
                                productName={p.name}
                                activeSize={activeSize}
                                onSelectLine={(size) =>
                                  setActiveSizeByProduct((prev) => ({ ...prev, [p.id]: size }))
                                }
                                testIdPrefix={`catalog-search-${p.id}`}
                              />
                            ) : null}
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {row ? (
                              <button
                                type="button"
                                data-testid={`catalog-search-minus-${p.id}`}
                                aria-label={row.specType === "area" ? "Remove from list" : "Decrease quantity or remove"}
                                onClick={() => decrementProductFromCatalogSearch(p, activeSize)}
                                className="w-8 h-8 flex items-center justify-center border border-neutral-300 rounded-sm bg-white hover:bg-neutral-100 text-neutral-800"
                              >
                                <Minus size={14} weight="bold" />
                              </button>
                            ) : null}
                            {row && row.specType === "area" ? (
                              <span
                                className="rounded-sm bg-violet-100 text-violet-800 border border-violet-200 text-[10px] font-bold px-2 py-1 leading-none"
                                data-testid={`catalog-search-area-badge-${p.id}`}
                              >
                                Area
                              </span>
                            ) : null}
                            {row && row.specType !== "area" ? (
                              <span
                                className="rounded-sm bg-orange-600 text-white text-xs font-bold font-mono tabular-nums min-w-[2.25rem] px-2 py-1 text-center leading-none"
                                data-testid={`catalog-search-qty-${p.id}`}
                              >
                                ×{row.quantity}
                              </span>
                            ) : null}
                            <button
                              type="button"
                              data-testid={`catalog-search-plus-${p.id}`}
                              aria-label="Increase quantity or add"
                              onClick={() => incrementProductFromCatalogSearch(p, activeSize)}
                              disabled={!!row && row.specType === "area"}
                              className="w-8 h-8 flex items-center justify-center border border-orange-500 rounded-sm bg-orange-50 hover:bg-orange-100 text-orange-700 disabled:opacity-40 disabled:pointer-events-none"
                            >
                              <Plus size={16} weight="bold" />
                            </button>
                          </div>
                        </div>
                      </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <DialogFooter className="p-4 pt-3 border-t border-neutral-200 bg-white sm:justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="catalog-search-done-btn"
                  className="rounded-sm border-neutral-300"
                  onClick={() => setCatalogSearchOpen(false)}
                >
                  Done
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {selectedCount > 0 ? (
            <div className="rounded-2xl px-5 py-4 mb-2 flex items-center gap-3" style={{ background: "rgba(255,122,0,0.08)", border: "1.5px solid rgba(255,122,0,0.2)" }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "#FF7A00" }}>
                <ShoppingCart size={16} weight="bold" style={{ color: "#fff" }} />
              </div>
              <p className="text-sm font-medium text-neutral-700">
                <span className="font-black" style={{ color: "#FF7A00" }}>Next:</span> open{" "}
                <span className="font-bold text-neutral-900">View Enquiry</span> to fill project details and submit.
              </p>
            </div>
          ) : null}
        </div>
      </section>

      {selectedCount > 0 ? (
        <button
          type="button"
          data-testid="view-enquiry-fab"
          onClick={goToRequirement}
          className="fixed bottom-6 right-4 sm:right-8 z-40 inline-flex items-center gap-3 pl-5 pr-2 py-2.5 rounded-full bg-[#FF7A00] hover:bg-[#e86800] text-white text-sm font-bold shadow-lg shadow-neutral-900/20 border border-[#e86800] transition-transform hover:scale-[1.02] active:scale-[0.98]"
        >
          <ShoppingCart size={22} weight="bold" className="shrink-0" aria-hidden />
          <span>View Enquiry</span>
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#FF7A00] text-sm font-extrabold tabular-nums">
            {cartBadgeTotal}
          </span>
        </button>
      ) : null}

      <Footer />
    </div>
  );
}
